// Calculator.jsx
import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  Fragment,
} from "react";

const btnValues = [
  ["C", "+-", "/"],
  [7, 8, 9, "X"],
  [4, 5, 6, "-"],
  [1, 2, 3, "+"],
  [0, ".", "="],
];

const btnValuesNumbers = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
  ["C", 0],
];

// 2026-10-27 · Importes (p. ej. «Total directo» del arqueo): misma botonera con
// la tecla decimal. Se muestra «,» y se entrega con punto (parseFloat).
const btnValuesDecimal = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
  ["C", 0, ","],
];

const toLocaleString = (num) =>
  Number(num).toLocaleString("en-US").replace(/,/g, " ");

const removeSpaces = (num) => num.toString().replace(/\s/g, "");

// ---------- Wrapper ----------
const wrapperStyle = {
  width: "100%",
  // si esto te bloquea el layout, quita la siguiente línea:
  // height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const keypadContainerStyle = {
  width: "100%",
  maxWidth: 500,
  padding: 16,
  boxSizing: "border-box",
};

const displayStyle = {
  width: "100%",
  minHeight: 60,
  marginBottom: 16,
  padding: "12px 16px",
  borderRadius: 12,
  border: "2px solid #4CAF50",
  fontSize: 26,
  textAlign: "right",
  boxSizing: "border-box",
  fontFamily:
    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const Wrapper = ({ children, value, justNumber }) => (
  <div style={wrapperStyle}>
    {justNumber ? (
      children
    ) : (
      <div style={keypadContainerStyle}>
        <div style={displayStyle}>{value ?? "0"}</div>
        {children}
      </div>
    )}
  </div>
);

// ---------- ButtonBox ----------
const ButtonBox = ({ children, justNumber }) => {
  const style = {
    width: "100%",
    display: "grid",
    gridTemplateColumns: justNumber ? "repeat(3, 1fr)" : "repeat(4, 1fr)",
    gridTemplateRows: justNumber
      ? "repeat(4, minmax(90px, 1fr))"
      : "repeat(5, minmax(90px, 1fr))",
    gap: 10,
  };

  return <div style={style}>{children}</div>;
};

// ---------- Button ----------
const baseButtonStyle = {
  width: "100%",
  height: "100%",
  border: "none",
  backgroundColor: "rgb(80, 60, 209)",
  color: "#fff",
  fontSize: 24,
  fontWeight: "bold",
  borderRadius: 12,
  cursor: "pointer",
  outline: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.15)",
  transition: "transform 0.08s ease-out, box-shadow 0.08s ease-out",
};

const deleteButtonExtraStyle = {
  backgroundColor: "rgb(243, 61, 29)",
};

const equalsButtonExtraStyle = {
  backgroundColor: "rgb(0, 150, 136)",
};

const emptyButtonExtraStyle = {
  backgroundColor: "#ffffff",
  color: "transparent",
  cursor: "default",
  boxShadow: "none",
};

const Button = ({ value, variant, style: styleProp, ...rest }) => {
  const [pressed, setPressed] = useState(false);

  let extraStyle = {};
  if (variant === "delete") extraStyle = deleteButtonExtraStyle;
  if (variant === "equals") extraStyle = equalsButtonExtraStyle;
  if (variant === "empty") extraStyle = emptyButtonExtraStyle;

  const pressedStyle = pressed
    ? {
        transform: "translateY(1px) scale(0.98)",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      }
    : {};

  const style = {
    ...baseButtonStyle,
    ...extraStyle,
    ...pressedStyle,
    ...styleProp,
  };

  return (
    <button
      type="button" // 👈 evita submit de formularios
      style={style}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      {...rest}
    >
      {value}
    </button>
  );
};

// ---------- Calculator (forwardRef) ----------
const Calculator = forwardRef(function Calculator(
  { callback = () => {}, justNumber, isMobile, allowDecimal = false },
  ref,
) {
  const [calc, setCalc] = useState({
    sign: "",
    num: 0,
    res: 0,
  });

  const currentDisplay =
    calc.num !== 0 && calc.num !== "" ? calc.num : calc.res || 0;

  const notify = (value) => {
    if (typeof callback === "function") {
      callback(value);
    }
  };

  const numClickHandler = (e) => {
    e.preventDefault();
    const value = e.target.innerHTML;

    if (justNumber) {
      // Con decimales, como mucho 2 (céntimos).
      if (allowDecimal && /\.\d{2}$/.test(String(calc.num))) return;
      const newNum =
        calc.num === 0 && value === "0"
          ? "0"
          : calc.num === 0 && value !== 0
            ? value
            : `${calc.num}${value}`;

      setCalc((prev) => {
        const next = {
          ...prev,
          num: newNum,
          res: !prev.sign ? 0 : prev.res,
        };
        notify(next.num);
        return next;
      });
      return;
    }

    if (removeSpaces(calc.num).length >= 16) return;

    const newNum =
      calc.num === 0 && value === "0"
        ? "0"
        : removeSpaces(calc.num) % 1 === 0
          ? toLocaleString(Number(removeSpaces(`${calc.num}${value}`)))
          : toLocaleString(`${calc.num}${value}`);

    setCalc((prev) => {
      const next = {
        ...prev,
        num: newNum,
        res: !prev.sign ? 0 : prev.res,
      };
      notify(next.num);
      return next;
    });
  };

  /** Tecla «,» del modo número con decimales: una sola coma; «,» al empezar = «0,». */
  const decimalClickHandler = (e) => {
    e.preventDefault();
    setCalc((prev) => {
      const cur = String(prev.num ?? 0);
      if (cur.includes(".")) return prev;
      const next = { ...prev, num: `${prev.num === 0 ? "0" : cur}.` };
      notify(next.num);
      return next;
    });
  };

  const commaClickHandler = (e) => {
    e.preventDefault();
    const value = e.target.innerHTML;

    setCalc((prev) => {
      const next = {
        ...prev,
        num: !prev.num.toString().includes(".")
          ? `${prev.num}${value}`
          : prev.num,
      };
      notify(next.num);
      return next;
    });
  };

  const signClickHandler = (e) => {
    e.preventDefault();
    const value = e.target.innerHTML;

    setCalc((prev) => {
      const next = {
        ...prev,
        sign: value,
        res: !prev.res && prev.num ? prev.num : prev.res,
        num: 0,
      };
      notify(next.res);
      return next;
    });
  };

  const equalsClickHandler = () => {
    setCalc((prev) => {
      if (!(prev.sign && prev.num)) return prev;

      const math = (a, b, sign) =>
        sign === "+"
          ? a + b
          : sign === "-"
            ? a - b
            : sign === "X"
              ? a * b
              : a / b;

      const newRes =
        prev.num === "0" && prev.sign === "/"
          ? "No se puede dividir entre 0"
          : toLocaleString(
              math(
                Number(removeSpaces(prev.res)),
                Number(removeSpaces(prev.num)),
                prev.sign,
              ),
            );

      const next = {
        ...prev,
        res: newRes,
        sign: "",
        num: 0,
      };
      notify(next.res);
      return next;
    });
  };

  const invertClickHandler = () => {
    setCalc((prev) => {
      const next = {
        ...prev,
        num: prev.num ? toLocaleString(removeSpaces(prev.num) * -1) : 0,
        res: prev.res ? toLocaleString(removeSpaces(prev.res) * -1) : 0,
        sign: "",
      };
      notify(next.num || next.res);
      return next;
    });
  };

  const percentClickHandler = () => {
    setCalc((prev) => {
      let num = prev.num ? parseFloat(removeSpaces(prev.num)) : 0;
      let res = prev.res ? parseFloat(removeSpaces(prev.res)) : 0;

      const next = {
        ...prev,
        num: (num /= 100),
        res: (res /= 100),
        sign: "",
      };
      notify(next.num || next.res);
      return next;
    });
  };

  const resetClickHandler = () => {
    setCalc({
      sign: "",
      num: 0,
      res: 0,
    });
    notify("");
  };

  const resetClickHandlerWithNoCallback = () => {
    setCalc({
      sign: "",
      num: 0,
      res: 0,
    });
  };

  // 👇 Aquí exponemos EXACTAMENTE estos nombres
  useImperativeHandle(ref, () => ({
    resetClickHandler,
    resetClickHandlerWithNoCallback,
  }));

  return isMobile ? (
    <Fragment></Fragment>
  ) : (
    <Wrapper value={currentDisplay} justNumber={justNumber}>
      <ButtonBox justNumber={justNumber}>
        {justNumber
          ? (allowDecimal ? btnValuesDecimal : btnValuesNumbers).flat().map((btn, i) => {
              const isDelete = btn === "C";
              const extraStyle = {};
              if (isDelete && !allowDecimal) extraStyle.gridColumn = "1 / 3";

              return (
                <Button
                  key={i}
                  value={btn}
                  variant={isDelete ? "delete" : undefined}
                  style={extraStyle}
                  onClick={btn === "C" ? resetClickHandler : btn === "," ? decimalClickHandler : numClickHandler}
                />
              );
            })
          : btnValues.flat().map((btn, i) => {
              const isEquals = btn === "=";
              const isDelete = btn === "C";

              const extraStyle = {};
              if (isEquals) extraStyle.gridColumn = "3 / 5";
              if (isDelete) extraStyle.gridColumn = "1 / 3";

              return (
                <Button
                  key={i}
                  value={btn}
                  variant={
                    isEquals ? "equals" : isDelete ? "delete" : undefined
                  }
                  style={extraStyle}
                  onClick={
                    btn === "C"
                      ? resetClickHandler
                      : btn === "+-"
                        ? invertClickHandler
                        : btn === "%"
                          ? percentClickHandler
                          : btn === "="
                            ? equalsClickHandler
                            : btn === "/" ||
                                btn === "X" ||
                                btn === "-" ||
                                btn === "+"
                              ? signClickHandler
                              : btn === "."
                                ? commaClickHandler
                                : numClickHandler
                  }
                />
              );
            })}
      </ButtonBox>
    </Wrapper>
  );
});

export default Calculator;
